import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { Button } from "../../components/ui";
import CentinelaAvatar from "../../components/base/CentinelaChat/CentinelaAvatar";
import VoiceOrb from "../../components/base/CentinelaChat/VoiceOrb";
import ChatMessageContent from "../../components/base/CentinelaChat/ChatMessageContent";
import useCentinelaConversation from "../../components/base/CentinelaChat/useCentinelaConversation";
import useCentinelaVoice from "../../components/base/CentinelaChat/useCentinelaVoice";
import {
  CHAT_CONTEXT_SUGGESTIONS,
  CHAT_SUGGESTION_ICONS,
  CHAT_SUGGESTION_KEYS,
} from "../../components/base/CentinelaChat/constants";
import type { ChatContext } from "../../components/base/CentinelaChat/constants";
import { useUserStore } from "../../store/userStore";
import { showDemoNotices } from "../monitoreo-ia/services/centinelaDemoMode";
import { buildDemoChatThreads } from "../monitoreo-ia/services/centinelaMocks";
import { CHAT_THREAD_GROUPS } from "../monitoreo-ia/types";
import type { ChatThreadGroup, ChatThreadSummary } from "../monitoreo-ia/types";
import {
  useAiChatStore,
  type ChatThreadSummary as StoredThreadSummary,
} from "../../store/aiChatStore";

const COMPOSER_MAX_HEIGHT = 140;

const DAY_MS = 24 * 60 * 60 * 1000;

const groupOf = (updatedAt: number | null): ChatThreadGroup => {
  if (!updatedAt) return "earlier";

  const startOfToday = new Date().setHours(0, 0, 0, 0);
  if (updatedAt >= startOfToday) return "today";
  return updatedAt >= startOfToday - DAY_MS ? "yesterday" : "earlier";
};

const toSidebarThread = (thread: StoredThreadSummary): ChatThreadSummary => ({
  id: thread.id,
  title: thread.title,
  preview: "",
  group: groupOf(thread.updated_at),
  icon: "ri-chat-3-line",
});

export default function ChatIaView() {
  const { t } = useTranslation("common");

  const user = useUserStore((state) => state.user);

  // Always use plataforma context - no manual selection
  const ACTIVE_CONTEXT: ChatContext["id"] = "plataforma";

  const {
    messages,
    welcome,
    isWelcoming,
    isLoading,
    isSimulated,
    sendMessage,
    clear: clearMessages,
    threads: storedThreads,
    isLoadingThreads,
    threadsError,
    loadThreads,
    openThread,
    removeThread,
  } = useCentinelaConversation({
    context: ACTIVE_CONTEXT,
    surface: "plataforma",
    operatorName: user?.first_name,
    autoStart: true,
  });

  const activeThreadId = useAiChatStore((state) => state.threadId);

  const [historyOpen, setHistoryOpen] = useState(true);
  const [suggestionsOpen, setSuggestionsOpen] = useState(true);
  const [inputMode, setInputMode] = useState<"text" | "voice">("text");
  const [draft, setDraft] = useState("");
  const [isOpeningThread, setIsOpeningThread] = useState(false);
  const [aiSuggestions, setAiSuggestions] = useState<Array<{ text: string; id: string }>>([]);
  const threadBeingOpenedRef = useRef<string | null>(null);
  const prevMessagesLengthRef = useRef(0);
  const threadsLoadedRef = useRef(false);
  const contextRefRef = useRef<ChatContext["id"]>(ACTIVE_CONTEXT);

  const contextSuggestions = CHAT_CONTEXT_SUGGESTIONS[ACTIVE_CONTEXT];

  const bottomRef = useRef<HTMLDivElement>(null);
  const spokenIdRef = useRef<string | null>(null);
  const composerRef = useRef<HTMLTextAreaElement>(null);

  // Detect context from user input

  // Keep context ref in sync
  useEffect(() => {
    contextRefRef.current = ACTIVE_CONTEXT;
  }, [ACTIVE_CONTEXT]);

  useEffect(() => {
    const node = composerRef.current;
    if (!node) return;

    node.style.height = "0px";
    node.style.height = `${Math.min(node.scrollHeight, COMPOSER_MAX_HEIGHT)}px`;
  }, [draft, inputMode]);

  // Auto-detect context while typing (no debounce to ensure context is set before submit)
  useEffect(() => {
    if (!draft.trim()) return;

    const detectedContext = null; // Context detection disabled
    console.log("[ChatIa] Typing detection:", { draft: draft.substring(0, 50), detectedContext, currentContext: ACTIVE_CONTEXT });
    if (detectedContext && detectedContext !== ACTIVE_CONTEXT) {
      console.log("[ChatIa] Context changed from", ACTIVE_CONTEXT, "to", detectedContext);
      // setSuggestionContext(detectedContext);
    }
  }, [draft, ACTIVE_CONTEXT]);

  // Context scroll function removed - no context selector

  const threads = useMemo<ChatThreadSummary[]>(
    () => (isSimulated ? buildDemoChatThreads(t) : storedThreads.map(toSidebarThread)),
    [isSimulated, storedThreads, t],
  );

  useEffect(() => {
    if (isSimulated) return;
    if (threadsLoadedRef.current) return;
    threadsLoadedRef.current = true;
    loadThreads();
  }, [isSimulated, loadThreads]);

  useEffect(() => {
    if (!isOpeningThread || threadBeingOpenedRef.current !== activeThreadId) return;

    // Se desactiva cuando los mensajes se actualizaron (thread cargado)
    if (messages.length !== prevMessagesLengthRef.current) {
      prevMessagesLengthRef.current = messages.length;
      threadBeingOpenedRef.current = null;
      setIsOpeningThread(false);
    }
  }, [messages.length, isOpeningThread, activeThreadId]);

  const assistantName = t("monitoreoIa.assistantName");

  const submit = useCallback(
    (text: string) => {
      const value = text.trim();
      if (!value || isLoading) return;

      // Auto-detect and switch context based on user input
      const detectedContext = null; // Context detection disabled
      const contextToUse = detectedContext || ACTIVE_CONTEXT;

      console.log("[ChatIa] Submit:", {
        message: value.substring(0, 50),
        detectedContext,
        currentSuggestionContext: ACTIVE_CONTEXT,
        contextToUse
      });

      if (detectedContext && detectedContext !== ACTIVE_CONTEXT) {
        console.log("[ChatIa] Updating state context from", ACTIVE_CONTEXT, "to", detectedContext);
        // setSuggestionContext(detectedContext);
      }

      setDraft("");
      // Pass detected context directly to sendMessage to ensure it uses the correct context
      console.log("[ChatIa] Calling sendMessage with context:", contextToUse);
      sendMessage(value, contextToUse);
    },
    [isLoading, sendMessage, ACTIVE_CONTEXT],
  );

  const voice = useCentinelaVoice(submit);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages.length, isLoading]);

  useEffect(() => {
    if (isLoading) return;

    const last = messages[messages.length - 1];
    if (!last || last.role !== "assistant" || !last.content) return;
    if (spokenIdRef.current === last.id) return;

    spokenIdRef.current = last.id;
    voice.speak(last.content, last.id);
  }, [isLoading, messages, voice]);

  useEffect(() => {
    const last = messages[messages.length - 1];
    if (!last || last.role !== "assistant" || !last.content) {
      setAiSuggestions([]);
      return;
    }

    try {
      let suggestions: Array<{ text: string; id: string }> = [];

      // First, try to extract from explicit [SUGGESTIONS] block
      const suggestionsRegex = /\[SUGGESTIONS\]([\s\S]*?)\[\/SUGGESTIONS\]/;
      const match = last.content.match(suggestionsRegex);

      console.log("[ChatIa] Extracting suggestions from AI response:", {
        foundSuggestionsBlock: !!match,
        messagePreview: last.content.substring(0, 100),
      });

      if (match && match[1]) {
        const suggestionsText = match[1].trim();
        const suggestionLines = suggestionsText
          .split("\n")
          .map((line) => line.trim())
          .filter((line) => line.length > 0);

        suggestions = suggestionLines.map((text, index) => ({
          text: text.replace(/^[-*•]\s*/, ""),
          id: `ai-suggestion-${index}`,
        }));

        console.log("[ChatIa] Extracted suggestions from block:", suggestions.length);
      } else {
        // If no explicit block, try to extract questions or generate contextual suggestions
        const contentLines = last.content
          .split("\n")
          .map((line) => line.trim())
          .filter((line) => line.length > 0);

        // Find lines that look like questions
        const questionLines = contentLines.filter((line) => line.match(/\?$/));

        if (questionLines.length > 0) {
          suggestions = questionLines.slice(-3).map((text, index) => ({
            text: text.replace(/\?$/, "").trim(),
            id: `ai-suggestion-${index}`,
          }));
        } else if (last.content.length > 0) {
          // Generate contextual suggestions based on keywords in the response
          const contextKeywords: Record<string, string[]> = {
            unidad: ["Ver ubicación en mapa", "Detalles de la unidad", "Generar reporte"],
            flota: ["Resumen completo de flota", "Unidades detenidas", "Unidades en movimiento"],
            viaje: ["Ver detalles del viaje", "Ruta del viaje", "Historial de viajes"],
            incidente: ["Ver detalles", "Alertas activas", "Historial de incidentes"],
            detenida: ["Ver ubicación en mapa", "Motivo del paro", "Tiempo parado"],
            circulando: ["Ver ruta actual", "Velocidad actual", "Destino"],
            velocidad: ["Historial de velocidades", "Excesos detectados", "Gráfico de viaje"],
            "km/h": ["Detalles de velocidad", "Alertas de exceso", "Reporte de conducción"],
            geocerca: ["Ver geocercas", "Editar límites", "Historial de salidas"],
            alerta: ["Ver detalles de alerta", "Alertas activas", "Historial de alertas"],
            envío: ["Detalles del envío", "Ruta de entrega", "Estado del pedido"],
            conductor: ["Perfil del conductor", "Historial de viajes", "Estadísticas"],
            reporte: ["Generar nuevo reporte", "Historial de reportes", "Exportar datos"],
          };

          const contentLower = last.content.toLowerCase();
          const matching: string[] = [];

          for (const [keyword, suggestionsForKeyword] of Object.entries(contextKeywords)) {
            if (contentLower.includes(keyword)) {
              matching.push(...suggestionsForKeyword);
            }
          }

          // Always show suggestions, even if no keywords match (fallback suggestions)
          if (matching.length === 0) {
            matching.push(
              "Ver resumen de flota",
              "Generar reporte",
              "Consultar estado actual"
            );
          }

          suggestions = matching.slice(0, 3).map((text, index) => ({
            text,
            id: `ai-suggestion-${index}`,
          }));
        }
      }

      console.log("[ChatIa] Final AI suggestions:", suggestions);
      setAiSuggestions(suggestions);
    } catch (e) {
      console.error("Failed to parse AI suggestions:", e);
      setAiSuggestions([]);
    }
  }, [messages]);

  const startNewChat = () => {
    voice.release();
    clearMessages();
    setDraft("");
    setInputMode("text");
  };

  const selectThread = (id: string) => {
    if (isSimulated || id === activeThreadId) return;
    voice.release();
    threadBeingOpenedRef.current = id;
    setIsOpeningThread(true);
    openThread(id);
  };

  const activeThread = threads.find((thread) => thread.id === activeThreadId);

  const conversationIntro =
    isSimulated && activeThread
      ? t("monitoreoIa.chatPage.threadNotice", { title: activeThread.title })
      : (activeThread?.title ??
        welcome ??
        t("monitoreoIa.chat.emptyTitle", { name: assistantName }));

  return (
    <div className="flex h-full w-full min-w-0">
      <aside
        className={`flex shrink-0 flex-col overflow-hidden border-line bg-app transition-all duration-300 ${
          historyOpen ? "w-[280px] border-r" : "w-0"
        }`}
      >
        <div className="shrink-0 p-4">
          <Button shape="pill" fullWidth onClick={startNewChat}>
            {t("monitoreoIa.chatPage.newChat")}
          </Button>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-2 pb-2">
          {!isSimulated && isLoadingThreads && threads.length === 0 && (
            <p className="px-3 py-6 text-center text-xs text-content-muted">
              {t("monitoreoIa.chat.history.loading")}
            </p>
          )}

          {!isSimulated && threadsError && (
            <p className="px-3 py-6 text-center text-xs text-error">
              {threadsError}
            </p>
          )}

          {!isSimulated && !isLoadingThreads && threads.length === 0 && !threadsError && (
            <p className="px-3 py-6 text-center text-xs text-content-muted">
              {t("monitoreoIa.chat.history.empty")}
            </p>
          )}

          {CHAT_THREAD_GROUPS.map((group) => {
            const groupThreads = threads.filter((thread) => thread.group === group);
            if (groupThreads.length === 0) return null;

            return (
              <div key={group} className="mb-4">
                <p className="mb-1.5 px-3 text-[10px] font-semibold uppercase tracking-wider text-content-faint">
                  {t(`monitoreoIa.chatPage.groups.${group}`)}
                </p>

                {groupThreads.map((thread) => (
                  <div
                    key={thread.id}
                    className={`group flex items-center gap-1 rounded-full pr-1.5 transition-colors ${
                      activeThreadId === thread.id ? "bg-subtle" : "hover:bg-subtle"
                    }`}
                  >
                    <button
                      type="button"
                      onClick={() => selectThread(thread.id)}
                      disabled={isOpeningThread}
                      className={`min-w-0 flex-1 rounded-full px-3 py-2.5 text-left transition-opacity ${
                        isOpeningThread ? "opacity-50 cursor-not-allowed" : ""
                      }`}
                    >
                      <span className="flex items-start gap-2">
                        <i className={`${thread.icon} mt-0.5 text-[13px] text-content-faint`} />
                        <span className="min-w-0 flex-1">
                          <span className="block truncate text-[12.5px] font-medium text-content">
                            {thread.title}
                          </span>
                          {thread.preview.length > 0 && (
                            <span className="mt-0.5 block truncate text-[11px] text-content-faint">
                              {thread.preview}
                            </span>
                          )}
                        </span>
                      </span>
                    </button>

                    {!isSimulated && (
                      <Button
                        variant="ghost"
                        size="xs"
                        iconOnly
                        onClick={() => removeThread(thread.id)}
                        title={t("monitoreoIa.chat.history.delete")}
                        aria-label={t("monitoreoIa.chat.history.delete")}
                        className="shrink-0 text-content-faint opacity-0 hover:text-error group-hover:opacity-100"
                        icon={<i className="ri-delete-bin-line" />}
                      />
                    )}
                  </div>
                ))}
              </div>
            );
          })}
        </div>

        <div className="shrink-0 border-t border-line p-3">
          <button
            type="button"
            onClick={() => setHistoryOpen(false)}
            className="flex w-full items-center gap-2 rounded-full px-3 py-2 text-xs text-content-secondary transition-colors hover:bg-subtle hover:text-content"
          >
            <i className="ri-side-bar-line text-sm" />
            {t("monitoreoIa.chatPage.hideHistory")}
          </button>
        </div>
      </aside>

      <div className="relative flex min-w-0 flex-1 flex-col">
        <header className="flex shrink-0 items-center justify-between gap-3 border-b border-line px-6 py-4">
          <div className="flex min-w-0 items-center gap-3">
            {!historyOpen && (
              <Button
                variant="ghost"
                size="sm"
                iconOnly
                shape="pill"
                onClick={() => setHistoryOpen(true)}
                title={t("monitoreoIa.chatPage.showHistory")}
                aria-label={t("monitoreoIa.chatPage.showHistory")}
                icon={<i className="ri-side-bar-line" />}
              />
            )}
            <CentinelaAvatar size="md" pulse />
            <div className="min-w-0">
              <h1 className="truncate text-[17px] font-extrabold tracking-tight text-content">
                {t("monitoreoIa.chatPage.title")}
              </h1>
              <p className="truncate text-[11px] text-content-secondary">
                {activeThread ? activeThread.title : t("monitoreoIa.chatPage.subtitle")}
              </p>
            </div>
          </div>

          <div className="flex shrink-0 items-center gap-2">
            <button
              type="button"
              onClick={voice.toggleVoiceOutput}
              disabled={isOpeningThread}
              className={`flex h-9 items-center gap-1.5 whitespace-nowrap rounded-full border px-3 text-[11px] font-medium transition-colors ${
                isOpeningThread ? "opacity-50 cursor-not-allowed" : ""
              } ${
                voice.voiceEnabled
                  ? "border-primary-500/30 bg-primary-500/10 text-primary-500"
                  : "border-line bg-surface text-content-secondary hover:bg-subtle"
              }`}
            >
              <i className={voice.voiceEnabled ? "ri-volume-up-line" : "ri-volume-mute-line"} />
              {t(voice.voiceEnabled ? "monitoreoIa.chatPage.voiceOn" : "monitoreoIa.chatPage.voiceOff")}
            </button>

            <button
              type="button"
              onClick={() => setSuggestionsOpen(!suggestionsOpen)}
              disabled={isOpeningThread}
              className={`flex h-9 items-center gap-1.5 whitespace-nowrap rounded-full border px-3 text-[11px] font-medium transition-colors ${
                isOpeningThread ? "opacity-50 cursor-not-allowed" : ""
              } ${
                suggestionsOpen
                  ? "border-primary-500/30 bg-primary-500/10 text-primary-500"
                  : "border-line bg-surface text-content-secondary hover:bg-subtle"
              }`}
            >
              <i className="ri-lightbulb-line" />
              {t("monitoreoIa.chatPage.suggestions")}
            </button>

            <button
              type="button"
              disabled={!voice.isSupported || isOpeningThread}
              onClick={() => {
                const next = inputMode === "text" ? "voice" : "text";
                setInputMode(next);
                next === "text" && voice.closeOrb();
              }}
              className={`flex h-9 items-center gap-1.5 whitespace-nowrap rounded-full border px-3 text-[11px] font-medium transition-colors disabled:opacity-50 ${
                inputMode === "voice"
                  ? "border-primary-500/30 bg-primary-500/10 text-primary-500"
                  : "border-line bg-surface text-content-secondary hover:bg-subtle"
              }`}
            >
              <i className={inputMode === "voice" ? "ri-mic-fill" : "ri-keyboard-line"} />
              {t(inputMode === "voice" ? "monitoreoIa.chat.modeVoice" : "monitoreoIa.chat.modeText")}
            </button>

            <Button
              variant="secondary"
              size="sm"
              iconOnly
              shape="pill"
              disabled={isOpeningThread}
              onClick={startNewChat}
              title={t("monitoreoIa.chatPage.newChat")}
              aria-label={t("monitoreoIa.chatPage.newChat")}
              icon={<i className="ri-refresh-line" />}
            />
          </div>
        </header>

        <div className={`relative min-h-0 flex-1 space-y-4 overflow-y-auto px-6 py-5 transition-all ${
          isOpeningThread ? "blur-sm pointer-events-none" : ""
        }`}>
          {voice.orbState !== "idle" && (
            <VoiceOrb
              state={voice.orbState}
              size={150}
              transcript={voice.interimTranscript}
              analyserNode={voice.analyserNode}
              onClose={() => {
                voice.closeOrb();
                setInputMode("text");
              }}
            />
          )}

          {isSimulated && showDemoNotices() && (
            <div className="rounded-xl border border-warning/30 bg-warning/10 px-4 py-3">
              <p className="text-xs text-content-secondary">
                {t("monitoreoIa.chat.simulatedNotice", { name: assistantName })}
              </p>
            </div>
          )}

          <div className="flex gap-3">
            <CentinelaAvatar size="md" className="mt-1" />
            <div className="min-w-0 max-w-[70%] flex-1 whitespace-pre-line rounded-2xl rounded-tl-none border border-line bg-subtle px-5 py-4 text-[13px] leading-relaxed text-content">
              {isWelcoming ? (
                <span
                  className="flex items-center gap-1.5 py-1"
                  aria-label={t("monitoreoIa.chat.thinking")}
                >
                  <span className="h-2 w-2 animate-bounce rounded-full bg-primary-500 [animation-delay:0ms]" />
                  <span className="h-2 w-2 animate-bounce rounded-full bg-primary-500 [animation-delay:150ms]" />
                  <span className="h-2 w-2 animate-bounce rounded-full bg-primary-500 [animation-delay:300ms]" />
                </span>
              ) : (
                conversationIntro
              )}
            </div>
          </div>

          {messages.map((message) => (
            <div
              key={message.id}
              className={`flex gap-3 ${message.role === "user" ? "flex-row-reverse" : ""}`}
            >
              {message.role === "assistant" ? (
                <CentinelaAvatar size="md" className="mt-1" />
              ) : (
                <span className="mt-1 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-content">
                  <i className="ri-user-3-line text-xs text-app" />
                </span>
              )}

              <div
                className={`min-w-0 max-w-[70%] flex-1 whitespace-pre-wrap rounded-2xl px-4 py-3.5 text-[13px] leading-relaxed ${
                  message.role === "user"
                    ? "rounded-tr-none bg-primary-500 text-on-primary"
                    : "rounded-tl-none border border-line bg-subtle text-content"
                }`}
              >
                {message.isLoading ? (
                  <span
                    className="flex items-center gap-1.5 py-1"
                    aria-label={t("monitoreoIa.chat.thinking")}
                  >
                    <span className="h-2 w-2 animate-bounce rounded-full bg-primary-500 [animation-delay:0ms]" />
                    <span className="h-2 w-2 animate-bounce rounded-full bg-primary-500 [animation-delay:150ms]" />
                    <span className="h-2 w-2 animate-bounce rounded-full bg-primary-500 [animation-delay:300ms]" />
                  </span>
                ) : (
                  <>
                    <ChatMessageContent content={message.content} />

                    {message.role === "assistant" && (
                      <span className="mt-2.5 flex items-center justify-between text-[10px] text-content-faint">
                        <span className="flex items-center gap-1.5">
                          <i className="ri-check-double-line text-[9px] text-primary-500" />
                          {t("monitoreoIa.chat.aiGenerated")}
                        </span>
                        {voice.speakingId === message.id && voice.isSpeaking ? (
                          <button
                            type="button"
                            onClick={voice.stopSpeaking}
                            className="flex items-center gap-1 whitespace-nowrap rounded-full bg-primary-500/10 px-2 py-0.5 text-[10px] font-medium text-primary-500 transition-colors hover:bg-primary-500/20"
                          >
                            <i className="ri-stop-circle-line text-[9px]" />
                            {t("monitoreoIa.chat.stopListening")}
                          </button>
                        ) : (
                          <button
                            type="button"
                            onClick={() => voice.speak(message.content, message.id)}
                            className="flex items-center gap-1 whitespace-nowrap rounded-full px-2 py-0.5 text-[10px] text-content-faint transition-colors hover:bg-primary-500/10 hover:text-primary-500"
                          >
                            <i className="ri-volume-up-line text-[9px]" />
                            {t("monitoreoIa.chat.listen")}
                          </button>
                        )}
                      </span>
                    )}
                  </>
                )}
              </div>
            </div>
          ))}

          <div ref={bottomRef} />
        </div>

        <div className="shrink-0 border-t border-line px-6 py-4">
          {inputMode === "text" ? (
            <form
              className="w-full"
              onSubmit={(event) => {
                event.preventDefault();
                submit(draft);
              }}
            >
              <div className="flex items-end gap-1.5 rounded-3xl border border-line bg-surface py-2 pl-5 pr-2 transition-colors focus-within:border-primary-500 focus-within:ring-2 focus-within:ring-primary-500/20">
                <textarea
                  ref={composerRef}
                  rows={1}
                  value={draft}
                  onChange={(event) => setDraft(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key !== "Enter" || event.shiftKey) return;
                    event.preventDefault();
                    submit(draft);
                  }}
                  placeholder={t("monitoreoIa.chatPage.placeholder")}
                  className="min-h-[28px] flex-1 resize-none self-center border-0 bg-transparent py-1 text-[13px] leading-relaxed text-content outline-none placeholder:text-content-faint"
                />

                <Button
                  variant="soft"
                  size="md"
                  shape="pill"
                  iconOnly
                  type="button"
                  disabled={!voice.isSupported}
                  onClick={() => setInputMode("voice")}
                  title={t("monitoreoIa.chat.modeVoice")}
                  aria-label={t("monitoreoIa.chat.modeVoice")}
                  icon={<i className="ri-mic-line text-sm" />}
                />
                <Button
                  type="submit"
                  size="md"
                  shape="pill"
                  iconOnly
                  loading={isLoading}
                  disabled={draft.trim().length === 0}
                  aria-label={t("monitoreoIa.chat.send")}
                  icon={<i className="ri-send-plane-fill text-sm" />}
                />
              </div>

              {draft.trim().length > 0 && (
                <p className="mt-2 px-2 text-[11px] text-content-faint">
                  {t("monitoreoIa.chat.composerHint")}
                </p>
              )}
            </form>
          ) : (
            <div className="flex w-full flex-col items-center gap-3">
              <button
                type="button"
                onClick={voice.toggleListening}
                className={`flex h-12 w-full items-center justify-center gap-3 whitespace-nowrap rounded-full px-8 text-sm font-medium shadow-sm transition-colors ${
                  voice.isListening
                    ? "bg-error text-white hover:brightness-95"
                    : "bg-primary-500 text-on-primary hover:bg-primary-400"
                }`}
              >
                {voice.isListening ? (
                  <span className="relative flex h-2.5 w-2.5">
                    <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-white/70" />
                    <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-white" />
                  </span>
                ) : (
                  <i className="ri-mic-fill" />
                )}
                {t(
                  voice.isListening
                    ? "monitoreoIa.chat.voice.stopHint"
                    : "monitoreoIa.chat.voice.startHint",
                  { name: assistantName },
                )}
              </button>

              {!voice.isListening && (
                <p className="text-[11px] text-content-faint">
                  {t("monitoreoIa.chatPage.voiceHint", { name: assistantName })}
                </p>
              )}
            </div>
          )}
        </div>

        {isOpeningThread && (
          <div className="absolute inset-0 flex items-center justify-center bg-app/30 backdrop-blur-sm z-50 rounded-lg">
            <div className="flex flex-col items-center gap-3">
              <div className="flex items-center gap-1.5">
                <span className="h-2 w-2 animate-bounce rounded-full bg-primary-500 [animation-delay:0ms]" />
                <span className="h-2 w-2 animate-bounce rounded-full bg-primary-500 [animation-delay:150ms]" />
                <span className="h-2 w-2 animate-bounce rounded-full bg-primary-500 [animation-delay:300ms]" />
              </div>
              <p className="text-xs text-content-secondary">{t("monitoreoIa.chat.loading")}</p>
            </div>
          </div>
        )}
      </div>

      <aside
        className={`flex shrink-0 flex-col overflow-hidden border-line bg-app transition-all duration-300 ${
          suggestionsOpen ? "w-[320px] border-l" : "w-0"
        }`}
      >
        <div className="min-h-0 flex-1 space-y-4 overflow-y-auto p-5">
          {/* Context selector section removed - using plataforma context by default */}

          <div className="flex min-h-0 flex-1 flex-col rounded-2xl border border-line bg-subtle p-4">
            <div className="mb-3 flex shrink-0 items-center gap-2">
              <i className="ri-lightbulb-line text-sm text-primary-500" />
              <h2 className="min-w-0 text-[12px] font-bold uppercase tracking-wider text-content">
                {t("monitoreoIa.chatPage.suggestions")}
              </h2>
            </div>

            <div className="min-h-0 flex-1 space-y-1.5 overflow-y-auto">
              {contextSuggestions.map((key) => (
                <button
                  key={key}
                  type="button"
                  disabled={isLoading}
                  onClick={() => submit(t(`monitoreoIa.chat.suggestions.${key}`))}
                  className="flex w-full items-center gap-2.5 rounded-full border border-line bg-app px-4 py-2.5 text-left text-[11.5px] leading-snug text-content-secondary transition-colors hover:border-primary-500/40 hover:text-content disabled:opacity-50"
                >
                  <i
                    className={`${CHAT_SUGGESTION_ICONS[key]} shrink-0 text-xs text-content-faint`}
                  />
                  {t(`monitoreoIa.chat.suggestions.${key}`)}
                </button>
              ))}

              {aiSuggestions.length > 0 && (
                <>
                  <div className="my-2 border-t border-line/50 pt-2" />
                  <div className="flex items-center gap-1.5 px-1 py-1">
                    <i className="ri-ai-generate text-sm text-primary-500" />
                    <span className="text-[10px] font-semibold uppercase tracking-wider text-content-faint">
                      Con IA
                    </span>
                  </div>
                  {aiSuggestions.map((suggestion) => (
                    <button
                      key={suggestion.id}
                      type="button"
                      disabled={isLoading}
                      onClick={() => submit(suggestion.text)}
                      className="flex w-full items-center gap-2.5 rounded-full border border-primary-500/30 bg-primary-500/5 px-4 py-2.5 text-left text-[11.5px] leading-snug text-primary-500 transition-colors hover:border-primary-500/60 hover:bg-primary-500/10 disabled:opacity-50"
                    >
                      <i className="ri-lightbulb-flash-line shrink-0 text-xs" />
                      {suggestion.text}
                    </button>
                  ))}
                </>
              )}
            </div>

            <div className="mt-3 shrink-0 border-t border-line pt-3">
              <div className="mb-2 flex items-center gap-2">
                <i className="ri-flashlight-line text-sm text-primary-500" />
                <h3 className="text-[12px] font-bold uppercase tracking-wider text-content">
                  {t("monitoreoIa.chat.quickActions")}
                </h3>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {CHAT_SUGGESTION_KEYS.map((key) => (
                  <button
                    key={key}
                    type="button"
                    disabled={isLoading}
                    onClick={() => submit(t(`monitoreoIa.chat.suggestions.${key}`))}
                    className="flex items-center gap-1.5 whitespace-nowrap rounded-full border border-line bg-app px-2.5 py-1.5 text-[10.5px] font-medium text-content-secondary transition-colors hover:border-primary-500/40 hover:bg-primary-500/10 hover:text-content disabled:opacity-50"
                  >
                    <i className={`${CHAT_SUGGESTION_ICONS[key]} text-[10.5px]`} />
                    {t(`monitoreoIa.chat.shortcuts.${key}`)}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      </aside>
    </div>
  );
}
